'use client';

import { useState } from 'react';
import type { SearchAlertBlockProps } from '../types';
import { theme, sectionColors } from '../theme';

/**
 * New-listing alert lead capture. CLIENT component (like HomeValuation, unlike
 * the plain server-rendered ContactForm POST) because it confirms the signup
 * inline without a full-page navigation.
 *
 * On submit it POSTs JSON to the tenant site's same-origin `/api/search-alert`
 * route, which forwards to the gateway's POST /search-alert and returns
 * `{ id }`. The lead/alert destination is resolved server-side per tenant —
 * never hardcoded in the block (CLAUDE.md non-negotiable #2). On success the
 * visitor sees a confirmation; on failure, a retry message.
 */

interface SearchAlertResponse {
  id?: string;
}

type Status = 'idle' | 'pending' | 'success' | 'error';

export function SearchAlertBlock(props: SearchAlertBlockProps) {
  const {
    heading = 'Get new-listing alerts',
    subheading,
    buttonText = 'Notify me',
    showPhone = true,
  } = props;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  const field = 'w-full rounded-md border border-black/15 bg-transparent px-3 py-2';

  const [status, setStatus] = useState<Status>('idle');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'pending') return;
    setStatus('pending');

    const form = e.currentTarget;
    const fd = new FormData(form);

    const priceMinRaw = String(fd.get('price_min') ?? '').trim();
    const priceMaxRaw = String(fd.get('price_max') ?? '').trim();
    const bedsRaw = String(fd.get('beds') ?? '').trim();
    const priceMin = priceMinRaw ? Number(priceMinRaw) : undefined;
    const priceMax = priceMaxRaw ? Number(priceMaxRaw) : undefined;
    const beds = bedsRaw ? Number(bedsRaw) : undefined;

    const payload: {
      name: string;
      email: string;
      phone: string;
      location: string;
      price_min?: number;
      price_max?: number;
      beds?: number;
    } = {
      name: String(fd.get('name') ?? '').trim(),
      email: String(fd.get('email') ?? '').trim(),
      phone: String(fd.get('phone') ?? '').trim(),
      location: String(fd.get('location') ?? '').trim(),
    };
    if (priceMin !== undefined && Number.isFinite(priceMin)) payload.price_min = priceMin;
    if (priceMax !== undefined && Number.isFinite(priceMax)) payload.price_max = priceMax;
    if (beds !== undefined && Number.isFinite(beds)) payload.beds = beds;

    try {
      const res = await fetch('/api/search-alert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      (await res.json()) as SearchAlertResponse;
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  return (
    <section
      id="search-alert"
      className="px-6 py-12"
      style={{ ...colors, fontFamily: theme.bodyFont }}
    >
      <div className="mx-auto max-w-xl">
        <h2 className="mb-2 text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
          {heading}
        </h2>
        {subheading ? <p className="mb-6 opacity-80">{subheading}</p> : null}

        {status === 'success' ? (
          <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <p>You&apos;re all set — we&apos;ll alert you when matching homes hit the market.</p>
          </div>
        ) : (
          <>
            {status === 'error' ? (
              <p className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                Sorry, something went wrong setting up your alert. Please try again.
              </p>
            ) : null}
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                name="location"
                placeholder="City, ZIP, or neighborhood"
                required
                className={field}
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="number"
                  name="price_min"
                  placeholder="Min price (optional)"
                  min={0}
                  className={field}
                />
                <input
                  type="number"
                  name="price_max"
                  placeholder="Max price (optional)"
                  min={0}
                  className={field}
                />
              </div>
              <input
                type="number"
                name="beds"
                placeholder="Beds (optional)"
                min={0}
                className={field}
              />
              <input type="text" name="name" placeholder="Your name" required className={field} />
              <input type="email" name="email" placeholder="you@example.com" required className={field} />
              {showPhone ? <input type="tel" name="phone" placeholder="Phone (optional)" className={field} /> : null}
              <button
                type="submit"
                disabled={status === 'pending'}
                className="rounded-md px-6 py-2 font-medium text-white hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: theme.primary }}
              >
                {status === 'pending' ? 'Setting up…' : buttonText}
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
}
